import * as Sentry from '@sentry/nextjs';
import { useMutation } from '@apollo/client/react';
import { useRouter } from 'next/navigation';
import { ListingFavoriteCreateDocument, ListingFavoriteDeleteDocument } from '@gql';
import { useNotification } from '@contexts/use-notification';
import { routes } from '@routes';
import { type FavoriteMutationOutcome, outcomeOfCreate, outcomeOfDelete } from './utils';

/**
 * Flips a listing's favorite on the server and resolves to the state the
 * server now holds, or `null` when it did not apply. On `null` the guest has
 * already been shown an error or sent to login.
 */
export function useToggleFavorite(): (listingId: string, isFavorited: boolean) => Promise<boolean | null> {
  const [createFavorite] = useMutation(ListingFavoriteCreateDocument);
  const [deleteFavorite] = useMutation(ListingFavoriteDeleteDocument);
  const {
    addNotification
  } = useNotification();
  const router = useRouter();
  function notifyFailure(): void {
    addNotification({
      type: 'error',
      title: 'Something went wrong...',
      message: 'Please try again later.'
    });
  }
  return async (listingId, isFavorited) => {
    const variables = {
      input: {
        listingId
      }
    };
    let outcome: FavoriteMutationOutcome;
    try {
      outcome = isFavorited ? outcomeOfDelete((await deleteFavorite({
        variables
      })).data) : outcomeOfCreate((await createFavorite({
        variables
      })).data);
    } catch {
      notifyFailure();
      return null;
    }
    switch (outcome.kind) {
      case 'applied':
        return outcome.isFavorited;
      case 'signed_out':
        router.push(routes.login, {
          scroll: false
        });
        return null;
      case 'rejected':
        Sentry.captureMessage('favorite.mutation_rejected', {
          level: 'error',
          tags: {
            action: isFavorited ? 'delete' : 'create',
            typename: outcome.typename
          },
          extra: {
            listingId
          }
        });
        notifyFailure();
        return null;
      default:
        {
          const unhandled: never = outcome;
          return unhandled;
        }
    }
  };
}